/**
 * FileCard - Card hien thi file voi thumbnail/icon theo loai
 * Co 2 variants: grid va list
 * Thumbnail duoc hien thi cho tat ca file types (PDF, Doc, Slides, Images, etc.)
 * Fallback: Google Drive thumbnail API -> Icon
 *
 * Selection mode: Khi selectionMode = true hoac hover, hien thi checkbox
 */
import { useState, useCallback } from 'react'
import clsx from 'clsx'
import {
  HiOutlineDocumentText,
  HiOutlineLink,
  HiOutlineExternalLink,
  HiOutlineDownload,
  HiOutlineEye,
  HiOutlinePhotograph,
  HiOutlineFilm,
  HiOutlineTable,
  HiOutlinePresentationChartBar,
  HiOutlineDocumentReport,
  HiOutlineFolder,
  HiOutlineMusicNote,
  HiOutlineCode,
  HiOutlineArchive,
  HiOutlineCheck,
} from 'react-icons/hi'
import driveService from '../../services/driveService'

// File type configurations with colors and icons
const FILE_TYPES = {
  pdf: {
    label: 'PDF',
    icon: HiOutlineDocumentReport,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
  spreadsheet: {
    label: 'Excel',
    icon: HiOutlineTable,
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
  },
  document: {
    label: 'Doc',
    icon: HiOutlineDocumentText,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
  presentation: {
    label: 'Slide',
    icon: HiOutlinePresentationChartBar,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
  },
  image: {
    label: 'Anh',
    icon: HiOutlinePhotograph,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
  },
  video: {
    label: 'Video',
    icon: HiOutlineFilm,
    color: 'text-pink-600',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
  },
  audio: {
    label: 'Audio',
    icon: HiOutlineMusicNote,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
  },
  archive: {
    label: 'ZIP',
    icon: HiOutlineArchive,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
  },
  code: {
    label: 'Code',
    icon: HiOutlineCode,
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
  },
  folder: {
    label: 'Folder',
    icon: HiOutlineFolder,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  default: {
    label: 'File',
    icon: HiOutlineDocumentText,
    color: 'text-gray-500',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
  },
}

/**
 * Get file type config based on mime type
 */
function getFileTypeConfig(mimeType) {
  const mime = (mimeType || '').toLowerCase()

  if (mime.includes('pdf')) return FILE_TYPES.pdf
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')) return FILE_TYPES.spreadsheet
  if (mime.includes('document') || mime.includes('word') || mime.includes('text/plain')) return FILE_TYPES.document
  if (mime.includes('presentation') || mime.includes('powerpoint')) return FILE_TYPES.presentation
  if (mime.includes('image')) return FILE_TYPES.image
  if (mime.includes('video')) return FILE_TYPES.video
  if (mime.includes('audio')) return FILE_TYPES.audio
  if (mime.includes('zip') || mime.includes('rar') || mime.includes('archive') || mime.includes('compressed')) return FILE_TYPES.archive
  if (mime.includes('folder')) return FILE_TYPES.folder
  if (mime.includes('javascript') || mime.includes('json') || mime.includes('html') || mime.includes('css')) return FILE_TYPES.code

  return FILE_TYPES.default
}

/**
 * Check if file type supports thumbnail from Google Drive
 * Google Drive can generate thumbnails for: images, PDFs, documents, spreadsheets, presentations
 */
function supportsThumbnail(mimeType) {
  const mime = (mimeType || '').toLowerCase()
  return (
    mime.includes('image') ||
    mime.includes('pdf') ||
    mime.includes('document') ||
    mime.includes('word') ||
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    mime.includes('presentation') ||
    mime.includes('powerpoint') ||
    mime.includes('video')
  )
}

/**
 * FileCard component
 * @param {Object} file - File data from API
 * @param {Function} onPreview - Handler for preview click
 * @param {Function} onCopyLink - Handler for copy link
 * @param {'grid'|'list'} variant - Display variant
 * @param {boolean} priority - If true, loads thumbnail eagerly (for first 6 files)
 * @param {boolean} selectionMode - If true, show checkbox for selection
 * @param {boolean} isSelected - If true, file is selected
 * @param {Function} onSelect - Handler for checkbox toggle (receives file, isSelected)
 */
function FileCard({
  file,
  onPreview,
  onCopyLink,
  variant = 'grid',
  priority = false,
  selectionMode = false,
  isSelected = false,
  onSelect,
}) {
  const [imageError, setImageError] = useState(false)
  const [useFallback, setUseFallback] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const fileType = getFileTypeConfig(file.mime_type)
  const IconComponent = fileType.icon

  // Get thumbnail URL with fallback strategy
  // Priority: 1. Backend proxy (best - uses Service Account) -> 2. stable_thumbnail_url -> 3. Icon
  const getThumbnailUrl = useCallback((size = 400) => {
    // Check if this file type supports thumbnails
    if (!supportsThumbnail(file.mime_type)) return null

    // If already tried and failed, return null
    if (imageError && useFallback) return null

    // First try: Use backend proxy (authenticates via Service Account)
    // This is the most reliable method for non-public files
    if (!useFallback && file.drive_id) {
      return driveService.getThumbnailUrl(file.drive_id, size)
    }

    // Fallback: Try stable thumbnail URL from API (direct Google URL)
    if (file.stable_thumbnail_url) {
      return file.stable_thumbnail_url.replace(/sz=w\d+/, `sz=w${size}`)
    }

    return null
  }, [file.stable_thumbnail_url, file.drive_id, file.mime_type, imageError, useFallback])

  // Handle thumbnail load error - try fallback
  const handleThumbnailError = useCallback(() => {
    if (!useFallback && file.drive_id) {
      // First error - try fallback URL
      setUseFallback(true)
      setIsLoading(true) // Reset loading for fallback attempt
    } else {
      // Fallback also failed - show icon
      setImageError(true)
      setIsLoading(false)
    }
  }, [useFallback, file.drive_id])

  // Handle thumbnail load success
  const handleThumbnailLoad = useCallback(() => {
    setIsLoading(false)
  }, [])

  // Handle preview
  const handlePreview = () => {
    if (onPreview) {
      onPreview(file)
    }
  }

  // Handle copy link
  const handleCopyLink = (e) => {
    e.stopPropagation()
    if (onCopyLink) {
      onCopyLink(file)
    }
  }

  // Handle open original
  const handleOpenOriginal = (e) => {
    e.stopPropagation()
    if (file.view_url) {
      driveService.trackAccess(file.drive_id)
      window.open(file.view_url, '_blank')
    }
  }

  // Handle download
  const handleDownload = (e) => {
    e.stopPropagation()
    if (file.download_url) {
      window.open(file.download_url, '_blank')
    } else if (file.drive_id) {
      window.open(`https://drive.google.com/uc?export=download&id=${file.drive_id}`, '_blank')
    }
  }

  // Handle checkbox toggle
  const handleCheckboxClick = (e) => {
    e.stopPropagation()
    if (onSelect) {
      onSelect(file, !isSelected)
    }
  }

  const thumbnailUrl = getThumbnailUrl()
  const showThumbnail = thumbnailUrl && !imageError

  // Grid variant - Card with icon/thumbnail
  if (variant === 'grid') {
    return (
      <div
        onClick={handlePreview}
        className={clsx(
          'group bg-white rounded-xl border overflow-hidden cursor-pointer transition-all duration-200',
          isSelected
            ? 'border-primary-500 ring-2 ring-primary-200 shadow-lg'
            : 'border-gray-100 hover:border-primary-300 hover:shadow-lg'
        )}
      >
        {/* Thumbnail/Icon area */}
        <div className="relative aspect-[4/3] overflow-hidden">
          {showThumbnail ? (
            <>
              {/* Skeleton loading placeholder */}
              {isLoading && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer" />
                </div>
              )}
              {/* Actual thumbnail */}
              <img
                src={thumbnailUrl}
                alt={file.name}
                className={clsx(
                  'w-full h-full object-cover group-hover:scale-105 transition-all duration-300',
                  isLoading ? 'opacity-0' : 'opacity-100'
                )}
                onLoad={handleThumbnailLoad}
                onError={handleThumbnailError}
                loading={priority ? 'eager' : 'lazy'}
                fetchPriority={priority ? 'high' : 'auto'}
              />
            </>
          ) : (
            <div className={clsx(
              'w-full h-full flex flex-col items-center justify-center',
              fileType.bg
            )}>
              <IconComponent className={clsx('w-12 h-12', fileType.color)} />
              <span className={clsx(
                'mt-2 text-xs font-medium uppercase tracking-wide',
                fileType.color
              )}>
                {fileType.label}
              </span>
            </div>
          )}

          {/* Selection checkbox - show on hover or when in selection mode */}
          <div
            className={clsx(
              'absolute top-2 right-2 z-10 transition-opacity duration-200',
              selectionMode || isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            )}
          >
            <button
              onClick={handleCheckboxClick}
              className={clsx(
                'w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all',
                isSelected
                  ? 'bg-primary-500 border-primary-500 text-white'
                  : 'bg-white/90 border-gray-300 hover:border-primary-400'
              )}
              title={isSelected ? 'Bo chon' : 'Chon de tai'}
            >
              {isSelected && <HiOutlineCheck className="w-4 h-4" />}
            </button>
          </div>

          {/* File type badge (only show if has thumbnail) */}
          {showThumbnail && (
            <div className="absolute top-2 left-2">
              <span className={clsx(
                'px-2 py-0.5 text-xs font-medium rounded-full',
                'bg-white/90 shadow-sm',
                fileType.color
              )}>
                {fileType.label}
              </span>
            </div>
          )}

          {/* Hover overlay with actions */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreview}
                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                title="Xem truoc"
              >
                <HiOutlineEye className="w-5 h-5 text-gray-700" />
              </button>
              <button
                onClick={handleOpenOriginal}
                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                title="Mo file goc"
              >
                <HiOutlineExternalLink className="w-5 h-5 text-gray-700" />
              </button>
              <button
                onClick={handleCopyLink}
                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                title="Copy link"
              >
                <HiOutlineLink className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="p-3">
          <p className="text-sm font-medium text-gray-800 truncate" title={file.name}>
            {file.name}
          </p>
          {file.file_size && (
            <p className="text-xs text-gray-400 mt-1">
              {driveService.formatFileSize(file.file_size)}
            </p>
          )}
        </div>
      </div>
    )
  }

  // List variant - Compact horizontal layout with small thumbnail
  const listThumbnailUrl = getThumbnailUrl(200) // Smaller size for list view

  return (
    <div
      onClick={handlePreview}
      className={clsx(
        'group flex items-center gap-3 p-3 rounded-lg border',
        'cursor-pointer transition-all',
        isSelected
          ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-200'
          : clsx('hover:border-primary-200 hover:bg-primary-50/50', fileType.border)
      )}
    >
      {/* Checkbox for selection */}
      <div
        className={clsx(
          'flex-shrink-0 transition-all duration-200',
          selectionMode || isSelected ? 'w-6 opacity-100' : 'w-0 opacity-0 group-hover:w-6 group-hover:opacity-100'
        )}
      >
        <button
          onClick={handleCheckboxClick}
          className={clsx(
            'w-5 h-5 rounded border-2 flex items-center justify-center transition-all',
            isSelected
              ? 'bg-primary-500 border-primary-500 text-white'
              : 'bg-white border-gray-300 hover:border-primary-400'
          )}
          title={isSelected ? 'Bo chon' : 'Chon de tai'}
        >
          {isSelected && <HiOutlineCheck className="w-3 h-3" />}
        </button>
      </div>

      {/* Thumbnail or Icon box */}
      <div className={clsx(
        'w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden relative',
        !listThumbnailUrl || imageError ? fileType.bg : 'bg-gray-100'
      )}>
        {listThumbnailUrl && !imageError ? (
          <>
            {/* Skeleton for list variant */}
            {isLoading && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse">
                <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer" />
              </div>
            )}
            <img
              src={listThumbnailUrl}
              alt={file.name}
              className={clsx(
                'w-full h-full object-cover transition-opacity duration-200',
                isLoading ? 'opacity-0' : 'opacity-100'
              )}
              onLoad={handleThumbnailLoad}
              onError={handleThumbnailError}
              loading={priority ? 'eager' : 'lazy'}
              fetchPriority={priority ? 'high' : 'auto'}
            />
          </>
        ) : (
          <IconComponent className={clsx('w-6 h-6', fileType.color)} />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700 truncate" title={file.name}>
          {file.name}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={clsx('text-xs font-medium', fileType.color)}>
            {fileType.label}
          </span>
          {file.file_size && (
            <>
              <span className="text-gray-300">·</span>
              <span className="text-xs text-gray-400">
                {driveService.formatFileSize(file.file_size)}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handleCopyLink}
          className="p-1.5 hover:bg-primary-100 rounded-md transition-colors"
          title="Copy link"
        >
          <HiOutlineLink className="w-4 h-4 text-primary-600" />
        </button>
        <button
          onClick={handleDownload}
          className="p-1.5 hover:bg-primary-100 rounded-md transition-colors"
          title="Tai ve"
        >
          <HiOutlineDownload className="w-4 h-4 text-primary-600" />
        </button>
        <button
          onClick={handleOpenOriginal}
          className="p-1.5 hover:bg-primary-100 rounded-md transition-colors"
          title="Mo file goc"
        >
          <HiOutlineExternalLink className="w-4 h-4 text-primary-600" />
        </button>
      </div>
    </div>
  )
}

export default FileCard
